import { useEffect } from 'react';

type LayerWithDefaultVisibility = {
  id: string;
  defaultVisible?: boolean | null;
  incidentId?: string | null;
};

type UseDefaultLayerVisibilityByIdParams = {
  layers: LayerWithDefaultVisibility[];
  setVisibilityById: React.Dispatch<
    React.SetStateAction<Map<string, boolean | null>>
  >;
  /** When set, layers owned by this incident start visible. */
  incidentFilterId?: string | null;
};

/**
 * A layer tied to the open incident starts on. Hub and federal layers keep
 * the stored default. A choice the user already made this session is left
 * alone.
 */
export function initialLayerVisible(
  layer: LayerWithDefaultVisibility,
  incidentFilterId?: string | null
): boolean {
  if (incidentFilterId && layer.incidentId === incidentFilterId) return true;
  return layer.defaultVisible ?? false;
}

/**
 * Initialize internal and external layer default visibility, using db column
 * when available.
 */
export function useDefaultLayerVisibilityById({
  layers,
  setVisibilityById,
  incidentFilterId,
}: UseDefaultLayerVisibilityByIdParams) {
  useEffect(() => {
    setVisibilityById((prev) => {
      const next = new Map(prev);
      let hasChanges = false;

      for (const layer of layers) {
        const layerId = String(layer.id);
        if (!next.has(layerId)) {
          next.set(layerId, initialLayerVisible(layer, incidentFilterId));
          hasChanges = true;
        }
      }

      return hasChanges ? next : prev;
    });
  }, [layers, setVisibilityById, incidentFilterId]);
}
